/**
 * Talking Heads Provider Constants
 *
 * Configuration for talking head providers (cloud and local).
 * Local providers require model installation and GPU.
 */

import sharedConstants from './shared_constants.json';
import { calculateInfiniteTalkTokens } from './videoSources';

export type TalkingHeadProvider =
  | 'infinitetalk'
  | 'musetalk'
  | 'latentsync'
  | 'wav2lip'
  | 'multitalk';

export type TalkingHeadProviderInfo = {
  id: TalkingHeadProvider;
  name: string;
  description: string;
  type: 'cloud' | 'local';
  minVramGb: number | null; // null for cloud
  tokensPerVideo: number | null; // null for local (free after install)
  speedFactor: number; // 1.0 = realtime, 0.5 = 2x faster
  qualityRating: 1 | 2 | 3 | 4 | 5;
  features: string[];
  modelSize: string | null; // null for cloud
  supported: boolean;
  /** Whether this provider can accept a real reference video instead of a still image. */
  acceptsVideo: boolean;
  /** Which input type this provider works best with (must match backend TALKING_HEAD_INPUT_CAPABILITIES). */
  recommendedInput: 'image' | 'video';
};

export const TALKING_HEAD_PROVIDERS: Record<TalkingHeadProvider, TalkingHeadProviderInfo> = {
  infinitetalk: {
    id: 'infinitetalk',
    name: 'InfiniteTalk',
    description: 'Cloud-based, highest quality',
    type: 'cloud',
    minVramGb: null,
    // What POST /api/v1/talking-heads/generate actually charges, derived from the
    // vendor price the same way estimate_talking_heads_tokens() derives it. The
    // literal 250 that was here priced the job off a $0.10 cost that no longer
    // exists anywhere in the backend -- InfiniteTalk is $0.50 per 720p request --
    // so the picker advertised a quarter of the bill.
    tokensPerVideo: calculateInfiniteTalkTokens(),
    speedFactor: 4.5, // ~45s for 10s video
    qualityRating: 5,
    features: ['talking', 'singing', 'multi-language'],
    modelSize: null,
    supported: true,
    acceptsVideo: false,
    recommendedInput: 'image',
  },
  musetalk: {
    id: 'musetalk',
    name: 'MuseTalk',
    description: 'Fast local, works on laptops',
    type: 'local',
    minVramGb: sharedConstants.constants.limits.MUSETALK_MIN_VRAM_GB as number,
    tokensPerVideo: null,
    speedFactor: 0.5, // 2x faster than realtime
    qualityRating: 4,
    features: ['talking', 'realtime', 'multi-language'],
    modelSize: '~2GB',
    supported: true,
    acceptsVideo: true,
    recommendedInput: 'image',
  },
  latentsync: {
    id: 'latentsync',
    name: 'LatentSync',
    description: 'Best local quality, 8GB+ VRAM',
    type: 'local',
    minVramGb: sharedConstants.constants.limits.LATENTSYNC_MIN_VRAM_GB as number,
    tokensPerVideo: null,
    speedFactor: 2.0, // 2x slower than realtime
    qualityRating: 5,
    features: ['talking', 'no-flicker', 'multi-language'],
    modelSize: '~6GB',
    supported: true,
    acceptsVideo: true,
    recommendedInput: 'video',
  },
  wav2lip: {
    id: 'wav2lip',
    name: 'Wav2Lip',
    description: 'Ultra-light, older GPUs OK',
    type: 'local',
    minVramGb: sharedConstants.constants.limits.WAV2LIP_MIN_VRAM_GB as number,
    tokensPerVideo: null,
    speedFactor: 0.3, // 3x faster than realtime
    qualityRating: 3,
    features: ['talking', 'dubbing', 'low-vram'],
    modelSize: '~400MB',
    supported: true,
    acceptsVideo: false,
    recommendedInput: 'image',
  },
  multitalk: {
    id: 'multitalk',
    name: 'MultiTalk',
    description: `Multi-person conversations (${sharedConstants.constants.limits.MULTITALK_MIN_VRAM_GB}GB+ VRAM)`,
    type: 'local',
    minVramGb: sharedConstants.constants.limits.MULTITALK_MIN_VRAM_GB as number,
    tokensPerVideo: null,
    speedFactor: 4.0,
    qualityRating: 5,
    features: ['multi-person', 'singing', 'cartoon', 'state-of-the-art'],
    modelSize: '~14GB',
    supported: true,
    acceptsVideo: false,
    recommendedInput: 'image',
  },
};

export const DEFAULT_TALKING_HEAD_PROVIDER: TalkingHeadProvider = 'infinitetalk';

/**
 * Get provider info by ID
 */
export function getTalkingHeadProviderInfo(
  providerId: TalkingHeadProvider
): TalkingHeadProviderInfo {
  return TALKING_HEAD_PROVIDERS[providerId];
}

/**
 * Get all providers sorted by type (cloud first) then quality
 */
export function getSortedTalkingHeadProviders(): TalkingHeadProviderInfo[] {
  return Object.values(TALKING_HEAD_PROVIDERS).sort((a, b) => {
    // Cloud first
    if (a.type !== b.type) {
      return a.type === 'cloud' ? -1 : 1;
    }
    // Then by quality (descending)
    return b.qualityRating - a.qualityRating;
  });
}

/**
 * Get local providers only
 */
export function getLocalTalkingHeadProviders(): TalkingHeadProviderInfo[] {
  return Object.values(TALKING_HEAD_PROVIDERS).filter((p) => p.type === 'local');
}

/**
 * Get cloud providers only
 */
export function getCloudTalkingHeadProviders(): TalkingHeadProviderInfo[] {
  return Object.values(TALKING_HEAD_PROVIDERS).filter((p) => p.type === 'cloud');
}

/**
 * Check if a provider requires GPU
 */
export function providerRequiresGpu(providerId: TalkingHeadProvider): boolean {
  return TALKING_HEAD_PROVIDERS[providerId].type === 'local';
}

/**
 * Check if available VRAM is sufficient for provider
 */
export function isVramSufficientForProvider(
  providerId: TalkingHeadProvider,
  availableVramGb: number | null
): boolean {
  const provider = TALKING_HEAD_PROVIDERS[providerId];
  if (provider.type === 'cloud') return true;
  if (availableVramGb === null) return false;
  if (provider.minVramGb === null) return true;
  return availableVramGb >= provider.minVramGb;
}
